<script lang="ts">
  import { get } from 'svelte/store';
  import { feed } from '../lib/stores/feed';
  import { filters } from '../lib/stores/filters';
  import { derive, clientVisible, runAction, type TriageAction } from '../lib/stores/triage';
  import { priceUSD, brandLabel, relativeAge, saleLabel, platformDisplayName } from '../lib/format';

  let {
    id = null,
    onClose,
    onNavigate,
  }: {
    id?: number | null;
    onClose?: () => void;
    onNavigate?: (id: number) => void;
  } = $props();

  let shotIdx = $state(0);

  const current = $derived(id == null ? null : ($feed.items.find((l) => l.id === id) ?? null));
  const d = $derived(current ? derive(current) : null);

  const images = $derived(
    current === null
      ? []
      : current.image_urls.length > 0
        ? current.image_urls
        : current.primary_image_url
          ? [current.primary_image_url]
          : [],
  );

  // ‹ › and the arrow keys walk this list: everything visible under the current
  // filter, plus the open listing (which opening just marked seen).
  const navList = $derived(
    $feed.items.filter((l) => l.id === id || clientVisible(derive(l), $filters.show)),
  );

  const specRows = $derived.by((): [string, string][] => {
    if (!current) return [];
    const rows: [string, string][] = [['Kiểu bán', saleLabel(current)]];
    rows.push(['Đăng', relativeAge(current.listed_at)]);
    if (current.seller_name) {
      const pct = current.seller_feedback_pct;
      rows.push([
        'Người bán',
        pct == null ? current.seller_name : `${current.seller_name} · ${pct}%`,
      ]);
    }
    if (current.item_location) rows.push(['Vị trí', current.item_location]);
    for (const [key, values] of Object.entries(current.item_specifics)) {
      if (values.length > 0) rows.push([key, values.join(', ')]);
    }
    return rows;
  });

  const matchedCodes = $derived.by((): string[] => {
    const trace = current?.match_trace;
    if (!trace) return [];
    const codes: string[] = [];
    if (trace.matched_brand) codes.push(trace.matched_brand);
    if (trace.matched_model) codes.push(trace.matched_model);
    for (const ref of trace.matched_refs ?? []) codes.push(ref);
    for (const keyword of trace.matched_keywords ?? []) codes.push(keyword);
    return codes;
  });

  // Every id change: reset the thumbnail strip and mark the listing seen.
  $effect(() => {
    const openId = id;
    shotIdx = 0;
    if (openId == null) return;
    const listing = get(feed).items.find((l) => l.id === openId);
    if (listing) void runAction(listing, 'view');
  });

  function step(delta: number): void {
    if (navList.length === 0) return;
    const at = navList.findIndex((l) => l.id === id);
    const from = at < 0 ? 0 : at;
    const next = (from + delta + navList.length) % navList.length;
    onNavigate?.(navList[next]!.id);
  }

  function onKeydown(event: KeyboardEvent): void {
    if (id == null) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    }
  }

  async function act(action: TriageAction): Promise<void> {
    if (!current) return;
    await runAction(current, action);
    if (action === 'hide') onClose?.();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div
  class="lb"
  class:open={current !== null}
  role="dialog"
  aria-modal="true"
  aria-label="Chi tiết listing"
>
  {#if current && d}
    <div class="lbcard">
      <div class="lbshot" class:is-empty={images.length === 0}>
        {#if images.length > 0}
          <img src={images[shotIdx] ?? images[0]} alt={current.title} />
        {:else}
          &#9200;
        {/if}
        <div class="lbnav">
          <button type="button" aria-label="listing trước" onclick={() => step(-1)}>&#8249;</button>
          <button type="button" aria-label="listing sau" onclick={() => step(1)}>&#8250;</button>
        </div>
        {#if images.length > 1}
          <div class="lbthumbs">
            {#each images as src, i (src)}
              <button
                type="button"
                class="lbthumb"
                class:active={i === shotIdx}
                aria-label="ảnh {i + 1}"
                onclick={() => (shotIdx = i)}
              ></button>
            {/each}
          </div>
        {/if}
      </div>

      <div class="lbbody">
        <p class="lbtitle">{current.title}</p>
        <div class="lbprice">
          {priceUSD(current.price_usd)}
          <small>{brandLabel(current)}</small>
        </div>

        <dl class="spec">
          {#each specRows as [term, value] (term)}
            <dt>{term}</dt>
            <dd>{value}</dd>
          {/each}
        </dl>

        {#if current.match_trace}
          <div class="matched">
            Lọt bộ lọc nhờ: {current.match_trace.tier_reason}
            {#if matchedCodes.length > 0}
              <br />
              {#each matchedCodes as code, i (code)}{#if i > 0}{' · '}{/if}<code>{code}</code>{/each}
            {/if}
            {#if current.match_trace.exclude_detail}
              <br />Thuộc nhóm loại trừ: {current.match_trace.exclude_detail}
            {/if}
          </div>
        {/if}

        <div class="lbacts">
          <button type="button" class="save" onclick={() => void act('save')}>
            {d.saved ? '★ Bỏ lưu' : '★ Lưu'}
          </button>
          <button type="button" class="hide" onclick={() => void act('hide')}>✕ Ẩn</button>
          <button type="button" class="open" onclick={() => void act('open')}>↗ Mở trên {platformDisplayName(current.platform)}</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .lbshot.is-empty {
    display: grid;
    place-items: center;
    color: var(--ink-dim);
    font-family: var(--mono);
    font-size: 44px;
  }
  .lbthumb {
    width: 34px;
    height: 26px;
    border-radius: 4px;
    background: var(--surface);
    border: 1px solid var(--line-strong);
    padding: 0;
  }
  .lbthumb.active {
    border-color: var(--accent);
  }
</style>
